/**
 * Bulk upload of contracts and legacy contracts (isEmeisBulk).
 * Mirrors api/v1/contractbulkimport/* (Emeis.Contracting.Service.BulkImport).
 */
export type BulkMode = "contract" | "legacy";

export const BULK_MODE_VALUE: Record<BulkMode, number> = { contract: 1, legacy: 2 };

export const BULK_RUN_STATUS = {
  Queued: 1,
  Running: 2,
  Completed: 3,
  CompletedWithErrors: 4,
  Failed: 5,
} as const;

export const BULK_ROW_STATUS = {
  Pending: 1,
  Processing: 2,
  Succeeded: 3,
  Failed: 4,
} as const;

/** A run that is over: it will not change again. */
export const isBulkRunFinished = (status: number): boolean =>
  status === BULK_RUN_STATUS.Completed ||
  status === BULK_RUN_STATUS.CompletedWithErrors ||
  status === BULK_RUN_STATUS.Failed;

export const isBulkRowFinished = (status: number): boolean =>
  status === BULK_ROW_STATUS.Succeeded || status === BULK_ROW_STATUS.Failed;

/** One party as typed into the sheet. */
export interface BulkRawParty {
  order: number;
  type: string;
  name: string;
  entityType: string;
  signingAuthorityName: string;
  signingAuthorityEmail: string;
  pocEmail: string;
  registeredAddress: string;
}

/** One sheet row as read: every value still text, nothing resolved. */
export interface BulkRawRow {
  /** The row's number in the sheet, as Excel shows it. */
  rowNumber: number;
  contractType: string;
  contractCategory: string;
  contractTitle: string;
  jobOrder: string;
  requestedByEmail: string;
  department: string;
  /** yyyy-MM-dd when the sheet's value could be read as a date; the sheet's own text otherwise. */
  startDate: string;
  endDate: string;
  /** The dates as the instants to store (midnight in the organisation's time zone). Set just before sending. */
  startDateIso?: string | null;
  endDateIso?: string | null;
  renewalReminderDays: string;
  annualContractValue: string;
  connectedContractNumbers: string[];
  notes: string;
  fileName: string;
  parties: BulkRawParty[];
  editorEmail: string;
  reviewerEmails: string[];
  approverEmails: string[];
  initiatorEmail: string;
}

/** The workflow roles picked once on the upload screen (member ids). */
export interface BulkBatchRoles {
  editorId: string | null;
  reviewerIds: string[];
  approverIds: string[];
  initiatorId: string | null;
}

export interface BulkFieldError {
  /** The template column the problem is in; empty when it concerns the row as a whole. */
  column: string;
  message: string;
}

export interface BulkRowResult {
  /** The sheet row; 0 for a problem with the sheet as a whole. */
  rowNumber: number;
  isValid: boolean;
  errors: BulkFieldError[];
}

export interface BulkValidateRequest {
  mode: number;
  batchRoles: BulkBatchRoles | null;
  rows: BulkRawRow[];
  fileNames: string[];
}

export interface BulkValidateResponse {
  rows: BulkRowResult[];
  readyCount: number;
  errorCount: number;
  unreferencedFiles: string[];
  /** How many more contracts the plan allows; null when unlimited. */
  allowanceRemaining: number | null;
  allowanceExceeded: boolean;
}

/** Whether a checked sheet may be started: every row valid, and within the plan. */
export const isBulkSheetClean = (validation: BulkValidateResponse | null): boolean =>
  !!validation && validation.rows.length > 0 && validation.errorCount === 0 && !validation.allowanceExceeded;

/** Where one row's document was put in storage. */
export interface BulkRowFile {
  rowNumber: number;
  fileKey: string;
  fileSize: number;
  mimeType: string;
}

export interface BulkStartRequest extends BulkValidateRequest {
  files: BulkRowFile[];
}

export interface BulkRun {
  id: string;
  mode: number;
  status: number;
  totalRows: number;
  processedRows: number;
  succeededRows: number;
  failedRows: number;
  createdByName: string | null;
  creationDate: string;
  startedDate: string | null;
  completedDate: string | null;
  lastMessage: string | null;
}

export interface BulkRunRow {
  rowNumber: number;
  status: number;
  message: string | null;
  contractId: string | null;
  contractNumber: string | null;
  fileName?: string | null;
  processedDate?: string | null;
}

export interface BulkPage<T> {
  count: number;
  data: T[];
}

/** One "bulk_import_progress" message from the SignalR hub. */
export interface BulkProgressEvent {
  runId: string;
  mode: number;
  runStatus: number;
  total: number;
  processed: number;
  succeeded: number;
  failed: number;
  /** The sheet row this is about; null for a line about the run itself. */
  rowNumber: number | null;
  rowStatus: number | null;
  contractId: string | null;
  contractNumber: string | null;
  message: string | null;
}
